// sf-preview-bridge v1
//
// Tells the site-factory workspace which page this preview is on, so the
// workspace's page chip follows links, redirects and back/forward inside the
// preview. Started by /instrumentation-client.ts.
//
// Inert everywhere else. It needs NEXT_PUBLIC_PREVIEW_MODE=true (only the
// factory's preview sets it; the export strips it) AND a parent frame, so a
// deployed site, or a preview opened in its own tab, never posts anything.
// Never throws: a failure here must not break the page.
//
// The one message, posted to the parent frame:
//   { source: "sf-preview", type: "route", path: "/wallet" }
// `path` is the pathname only. No query string or hash: the workspace wants
// the page, and a query can carry things (reset tokens, emails) that have no
// business leaving the frame.
//
// Owned by site-factory (lib/preview is off-limits to the redesign agents):
// the workspace reads exactly this shape, and a backfill script replaces
// copies by the version on the first line.

let last: string | null = null;

function framedPreview(): boolean {
  // Spelled out in full: Next inlines NEXT_PUBLIC_* only for the literal.
  if (process.env.NEXT_PUBLIC_PREVIEW_MODE !== "true") return false;
  return typeof window !== "undefined" && window.parent !== window;
}

/** Post the page `url` points at (default: where we are), once per change. */
export function reportPreviewRoute(url?: string): void {
  try {
    if (!framedPreview()) return;
    const target = new URL(url ?? window.location.href, window.location.href);
    const path = target.pathname;
    if (target.origin !== window.location.origin || path === last) return;
    // "*": the workspace runs on another origin this page can't know, and
    // the path of a demo store is nothing to hide from whoever framed it.
    window.parent.postMessage({ source: "sf-preview", type: "route", path }, "*");
    last = path;
  } catch {
    // Never let the bridge break the site.
  }
}

/**
 * Report the first page, then every URL the router commits: back/forward,
 * and (where the Navigation API exists) redirects and replaces that
 * onRouterTransitionStart announced under another path.
 */
export function startPreviewBridge(): void {
  try {
    if (!framedPreview()) return;
    reportPreviewRoute();
    window.addEventListener("popstate", () => reportPreviewRoute());
    const navigation = (window as { navigation?: EventTarget }).navigation;
    navigation?.addEventListener("currententrychange", () => reportPreviewRoute());
  } catch {
    // Never let the bridge break the site.
  }
}
